/**
 * "My Queries" — the cross-period inbox.
 *
 * Without this the feature would only work for somebody who happens to have the right
 * frozen month open: a query raised on 2026-05 and due on Friday would be invisible to
 * anyone looking at 2026-06. This lists them regardless of period, newest due date first,
 * with the overdue ones on top.
 *
 * Booking-level rows are filtered server-side by [Reporting].[BabDetailAccess] — being
 * the assignee of a posting query does not by itself grant sight of the posting.
 */
import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, CalendarClock, Inbox, ExternalLink } from 'lucide-react';
import { babReportApi } from '../../services/babReportApi';
import { STATUS_CHIP, STATUS_LABEL, isOverdue } from './BabQueryPane';
import type { BabQueryInboxItem, BabQueryStatus } from '../../types/babReport';

type Scope = 'assigned' | 'raised' | 'all';

const SCOPES: { key: Scope; label: string; hint: string }[] = [
    { key: 'assigned', label: 'Addressed to me', hint: 'Queries somebody is waiting for me to answer' },
    { key: 'raised', label: 'Raised by me', hint: 'Queries I asked' },
    { key: 'all', label: 'Both', hint: 'Everything I am involved in' },
];

const STATUS_OPTIONS: { value: BabQueryStatus | 'open' | ''; label: string }[] = [
    { value: 'open', label: 'Still open' },
    { value: '', label: 'Any status' },
    { value: 'Open', label: 'Open' },
    { value: 'InProgress', label: 'In progress' },
    { value: 'Answered', label: 'Answered' },
    { value: 'Closed', label: 'Closed' },
    { value: 'Rejected', label: 'Rejected' },
];

function formatDate(value: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

interface Props {
    /** Opens the query in the report panel — same deep link the mails carry. */
    onOpen: (item: BabQueryInboxItem) => void;
}

export function BabQueryInboxTab({ onOpen }: Props) {
    const [scope, setScope] = useState<Scope>('assigned');
    const [status, setStatus] = useState<BabQueryStatus | 'open' | ''>('open');
    const [overdueOnly, setOverdueOnly] = useState(false);
    const [items, setItems] = useState<BabQueryInboxItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await babReportApi.getQueryInbox({
                scope,
                status: status || undefined,
                overdue: overdueOnly,
            });
            setItems(res);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load your queries');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [scope, status, overdueOnly]);

    useEffect(() => { void load(); }, [load]);

    const overdueCount = items.filter(isOverdue).length;

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex flex-wrap gap-4 items-end">
                    <div>
                        <label className="text-xs font-medium text-gray-500 block mb-1">Show</label>
                        <div className="flex rounded-md border overflow-hidden">
                            {SCOPES.map(s => (
                                <button
                                    key={s.key}
                                    title={s.hint}
                                    onClick={() => setScope(s.key)}
                                    className={`px-3 py-1.5 text-sm ${scope === s.key
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-500 block mb-1">Status</label>
                        <select
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                            value={status}
                            onChange={e => setStatus(e.target.value as BabQueryStatus | 'open' | '')}
                        >
                            {STATUS_OPTIONS.map(o => (
                                <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-gray-700 pb-1.5">
                        <input
                            type="checkbox"
                            checked={overdueOnly}
                            onChange={e => setOverdueOnly(e.target.checked)}
                            className="rounded"
                        />
                        Overdue only
                    </label>

                    <button
                        onClick={() => void load()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 ml-auto"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                </div>

                {overdueCount > 0 && (
                    <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5">
                        <AlertTriangle className="h-4 w-4" />
                        {overdueCount} {overdueCount === 1 ? 'query is' : 'queries are'} past the requested date.
                    </p>
                )}
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {loading ? (
                    <p className="p-6 text-sm text-gray-500">Loading…</p>
                ) : items.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">
                        <Inbox className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">Nothing here. No queries match this filter.</p>
                    </div>
                ) : (
                    <div className="overflow-auto max-h-[calc(100vh-320px)]">
                        <table className="min-w-full text-sm">
                            {/* `sticky` sits on the cells, not on <thead> — a sticky thead is
                                ignored while `border-collapse` is in play in several browsers. */}
                            <thead className="text-xs uppercase tracking-wide text-gray-500">
                                <tr>
                                    {['Status', 'Subject', 'Position', 'Period', 'Requested by', 'Asked by', 'Responsible'].map(h => (
                                        <th key={h} className="sticky top-0 z-10 bg-gray-50 text-left px-4 py-2 font-medium">{h}</th>
                                    ))}
                                    <th className="sticky top-0 z-10 bg-gray-50 px-4 py-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {items.map(q => {
                                    const late = isOverdue(q);
                                    return (
                                        <tr key={q.queryId} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <span className={`text-[11px] font-medium rounded px-1.5 py-0.5 ${STATUS_CHIP[q.status]}`}>
                                                    {STATUS_LABEL[q.status]}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2">
                                                <span className="font-medium text-gray-900">{q.subject}</span>
                                                {q.replyCount > 0 && (
                                                    <span className="ml-2 text-xs text-gray-400">
                                                        {q.replyCount} repl{q.replyCount === 1 ? 'y' : 'ies'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                                                {q.lineCode}{q.lineName ? ` ${q.lineName}` : ''}
                                                {q.accountCode && (
                                                    <span className="text-gray-400">
                                                        {' · '}{(q.sourceCompanyCode ?? '').toUpperCase()} {q.accountCode}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                                                {q.periodYear}-{String(q.periodMonth).padStart(2, '0')}
                                            </td>
                                            <td className={`px-4 py-2 whitespace-nowrap ${late ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                                                <span className="inline-flex items-center gap-1">
                                                    {late ? <AlertTriangle className="h-3.5 w-3.5" /> : <CalendarClock className="h-3.5 w-3.5 text-gray-400" />}
                                                    {formatDate(q.dueDate)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{q.createdBy}</td>
                                            <td className="px-4 py-2 text-gray-600">{q.assignees.join(', ') || '—'}</td>
                                            <td className="px-4 py-2 text-right">
                                                <button
                                                    onClick={() => onOpen(q)}
                                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                                >
                                                    Open <ExternalLink className="h-3 w-3" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
